import { API_URL } from '../config';

interface User {
  _id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  country: string;
  totalHikes?: number;
  totalLength?: number;
  totalElevation?: number;
  totalCountries?: number;
  createdAt?: string;
  achievements?: Array<{
    achievement: {
      _id: string;
      name: string;
      description: string;
      icon: string;
      tier: 'bronze' | 'silver' | 'gold' | 'platinum';
    };
    earnedAt: string;
  }>;
  friends?: Array<{ _id: string }>;
}

interface AuthResponse {
  user: User;
  message: string;
  expiresIn: number;
  accessToken?: string;
  refreshToken?: string;
}

class AuthService {
  private refreshPromise: Promise<boolean> | null = null;

  // Get user data from localStorage (primary method)
  getUserFromCookie(): User | null {
    try {
      // Primary: Get from localStorage
      const userFromStorage = localStorage.getItem('user');
      if (userFromStorage) {
        const user = JSON.parse(userFromStorage);
        return user;
      }
      
      // Fallback: Try cookies
      const userData = this.getCookie('userData');
      if (userData) {
        const user = JSON.parse(userData);
        // Store in localStorage for future use
        localStorage.setItem('user', userData);
        return user;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user data:', error);
      return null;
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const user = this.getUserFromCookie();
    return !!user;
  }

  // Login with credentials
  async login(loginId: string, password: string): Promise<User> {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for cookies
      body: JSON.stringify({ loginId, password }),
    });

    if (!response.ok) {
      let errorMessage = 'Login failed';
      let errorData: any = {};
      
      try {
        errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (parseError) {
        // Handle non-JSON responses (like rate limiting)
        if (response.status === 429) {
          errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
        } else if (response.status === 423) {
          errorMessage = 'Account temporarily locked due to too many failed attempts.';
        } else {
          errorMessage = `Login failed (${response.status}): ${response.statusText}`;
        }
      }
      
      // Create enhanced error with rate limiting info
      const error = new Error(errorMessage);
      (error as any).status = response.status;
      (error as any).retryAfter = errorData.retryAfter;
      (error as any).lockoutUntil = errorData.lockoutUntil;
      (error as any).message = errorMessage;
      
      throw error;
    }

    const data: AuthResponse = await response.json();
    
    // Store tokens and user data in localStorage
    if (data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
    }
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    return data.user;
  }

  // Register new user
  async register(
    username: string, 
    email: string, 
    password: string, 
    firstName: string, 
    lastName: string, 
    country: string
  ): Promise<User> {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ 
        username, 
        email, 
        password, 
        firstName, 
        lastName, 
        country 
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Registration failed';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
      } catch (parseError) {
        // Handle non-JSON responses (like rate limiting)
        if (response.status === 429) {
          errorMessage = 'Too many registration attempts. Please wait a few minutes and try again.';
        } else {
          errorMessage = `Registration failed (${response.status}): ${response.statusText}`;
        }
      }
      throw new Error(errorMessage);
    }

    const data: AuthResponse = await response.json();
    return data.user;
  }

  // Logout
  async logout(): Promise<void> {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear any client-side data
      this.clearClientSideData();
    }
  }

  // Refresh access token
  async refreshToken(): Promise<boolean> {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    const result = await this.refreshPromise;
    this.refreshPromise = null;
    return result;
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
  
        this.clearClientSideData();
        return false;
      }

      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${refreshToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.accessToken) {
          localStorage.setItem('accessToken', data.accessToken);

          return true;
        }
      }
      

      this.clearClientSideData();
      return false;
    } catch (error) {
      console.error('Error refreshing token:', error);
      this.clearClientSideData();
      return false;
    }
  }

  // Get cookie value by name (fallback method)
  private getCookie(name: string): string | null {
    const value = `; ${document.cookie}`;

    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      const cookieValue = parts.pop()?.split(';').shift() || null;

      return cookieValue;
    }

    return null;
  }

  // Clear client-side data
  private clearClientSideData(): void {
    // Clear any localStorage data if it exists
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    
    // Clear any sessionStorage data
    sessionStorage.clear();
    
    // Reload the page to clear any in-memory state
    window.location.href = '/login';
  }

  // Check if token needs refresh (client-side estimation)
  shouldRefreshToken(): boolean {
    // This is a client-side estimation since we can't access httpOnly cookies
    // The server will handle actual token validation and refresh
    return false; // Let the server handle this
  }

  // Get current user
  getCurrentUser(): User | null {
    return this.getUserFromCookie();
  }

  // Update user data (called when user data changes)
  updateUserData(user: User): void {
    // User data is managed by localStorage
    localStorage.setItem('user', JSON.stringify(user));

  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;